import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import api, { checklistAPI, staffAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import DeleteIconButton from '../components/DeleteIconButton';
import './MyChecklists.css';

/**
 * Client Checklists — list all checklist groups (default all staff; SC sees only their assigned clients).
 * Filters: client search, service coordinator, checklist type, closed (yes/no).
 * Consistent with AT packets, file reviews, client service needs.
 */
function MyChecklists() {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hintExpanded, setHintExpanded] = useState(false);
  const [showAddHint, setShowAddHint] = useState(false);
  const [roleCode, setRoleCode] = useState(null);
  const [types, setTypes] = useState([]);
  const [staffList, setStaffList] = useState([]);

  // Filters above column headings
  const [clientSearch, setClientSearch] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [closedFilter, setClosedFilter] = useState('open'); // 'open' (default) | 'all' | 'closed'

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (roleCode !== 'SERVICE_COORDINATOR') {
        params.allStaff = true;
      }
      const res = await checklistAPI.getMine(params);
      setList(res.data?.data ?? []);
    } catch (err) {
      const msg = err.response?.status === 404
        ? 'Checklist feature is not enabled for this tenant.'
        : (err.response?.data?.error || err.message);
      setError(msg);
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [roleCode]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/whoami');
        setRoleCode(res.data?.membership?.role_code ?? null);
      } catch {
        setRoleCode(null);
      }
    })();
  }, []);

  useEffect(() => {
    if (roleCode == null) return;
    load();
  }, [roleCode, load]);

  useEffect(() => {
    (async () => {
      try {
        const [typesRes, staffRes] = await Promise.all([
          checklistAPI.getTypes(),
          staffAPI.getAll({ active: 'true' }),
        ]);
        setTypes(typesRes.data?.data ?? []);
        setStaffList(staffRes.data?.data ?? []);
      } catch {
        setTypes([]);
        setStaffList([]);
      }
    })();
  }, []);

  const clientName = (row) => {
    const parts = [row.client_last_name, row.client_first_name].filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  };

  const assigneeName = (row) => {
    if (row.assignee_id == null) return '—';
    const parts = [row.assignee_first_name, row.assignee_last_name].filter(Boolean);
    return parts.length ? parts.join(' ') : '—';
  };

  const filteredList = useMemo(() => {
    let out = list;
    const clientLower = clientSearch.trim().toLowerCase();
    if (clientLower) {
      out = out.filter((row) => {
        const name = clientName(row).toLowerCase();
        return name.includes(clientLower);
      });
    }
    if (staffFilter) {
      out = out.filter((row) => String(row.assignee_id) === String(staffFilter));
    }
    if (typeFilter) {
      out = out.filter((row) => String(row.checklist_type_id) === String(typeFilter));
    }
    if (closedFilter === 'open') {
      out = out.filter((row) => !row.closed && row.referral_is_open);
    } else if (closedFilter === 'closed') {
      out = out.filter((row) => !!row.closed);
    }
    return out;
  }, [list, clientSearch, staffFilter, typeFilter, closedFilter]);

  const openChecklist = (row) => {
    navigate(tenantPath(`/referral/${row.referral_id}/checklists`), {
      state: { clientId: row.client_id },
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this checklist?')) return;
    try {
      await checklistAPI.delete(id);
      setList((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to delete checklist');
    }
  };

  const canCreateChecklist = hasPermission('checklists.create');
  const canDeleteChecklist = hasPermission('checklists.delete');
  const colCount = 4 + (canDeleteChecklist ? 1 : 0);

  return (
    <div className="my-checklists-page">
      <div className="header">
        <div className="my-checklists-header-row">
          <button
            type="button"
            className="my-checklists-title-with-hint"
            onClick={() => setHintExpanded((prev) => !prev)}
            aria-expanded={hintExpanded}
            aria-label={hintExpanded ? 'Hide description' : 'Show description'}
          >
            Client Checklists
            <span className="my-checklists-hint-icon" aria-hidden>ⓘ</span>
          </button>
          {canCreateChecklist && (
            <button
              type="button"
              className="my-checklists-btn-new"
              onClick={() => setShowAddHint(true)}
            >
              + New Checklist
            </button>
          )}
        </div>
        {hintExpanded && (
          <p className="my-checklists-hint-text">
            All checklists for this tenant. Use the filters above the table to narrow by client, service coordinator, type, or closed status.
          </p>
        )}
      </div>

      {showAddHint && (
        <div className="my-checklists-add-hint-overlay" onClick={() => setShowAddHint(false)} role="dialog" aria-modal="true" aria-labelledby="add-checklist-hint-title">
          <div className="my-checklists-add-hint-content" onClick={(e) => e.stopPropagation()}>
            <h3 id="add-checklist-hint-title">Add a checklist</h3>
            <p>Checklists are added on a client's referral. On the Dashboard, click a client in the Tracking table to open that client, then use the <strong>Checklists</strong> tab to add one.</p>
            <div className="my-checklists-add-hint-actions">
              <button type="button" className="my-checklists-btn-new" onClick={() => { setShowAddHint(false); navigate(tenantPath('/dashboard')); }}>
                Go to Dashboard
              </button>
              <button type="button" className="my-checklists-add-hint-cancel" onClick={() => setShowAddHint(false)}>Stay</button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="my-checklists-error">{error}</div>
      )}

      {!loading && list.length === 0 && !error && (
        <div className="my-checklists-empty">
          <p className="my-checklists-empty-title">You don't have any checklists yet</p>
          <p className="my-checklists-empty-text">
            Checklists are created for referrals (e.g. intake, IFSP). When you have clients with checklist assignments, they'll appear here.
          </p>
        </div>
      )}

      {(loading || list.length > 0) && (
      <>
      <div className="my-checklists-count">
        {loading && list.length === 0
          ? 'Loading…'
          : filteredList.length === 0
            ? '0 Client checklists'
            : filteredList.length === 1
              ? '1 Client checklist'
              : `${filteredList.length} Client checklists`}
      </div>
      <div className="my-checklists-table-wrap">
        <table className="my-checklists-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Assigned SC</th>
              <th>Checklist type</th>
              <th>Closed</th>
              {canDeleteChecklist && <th className="col-actions">Delete</th>}
            </tr>
            <tr className="my-checklists-filter-row">
              <th>
                <input
                  type="text"
                  className="my-checklists-filter-input"
                  placeholder="Search client..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  aria-label="Filter by client name"
                />
              </th>
              <th>
                <select
                  className="my-checklists-filter-select"
                  value={staffFilter}
                  onChange={(e) => setStaffFilter(e.target.value)}
                  aria-label="Filter by service coordinator"
                >
                  <option value="">All</option>
                  {staffList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {[s.first_name, s.last_name].filter(Boolean).join(' ') || `Staff ${s.id}`}
                    </option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  className="my-checklists-filter-select"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  aria-label="Filter by checklist type"
                >
                  <option value="">All</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.type_name || t.type_code || '—'}
                    </option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  className="my-checklists-filter-select"
                  value={closedFilter}
                  onChange={(e) => setClosedFilter(e.target.value)}
                  aria-label="Filter by closed status"
                >
                  <option value="all">All</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </th>
              {canDeleteChecklist && <th className="col-actions" />}
            </tr>
          </thead>
          <tbody>
            {loading && list.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="no-data">Loading…</td>
              </tr>
            ) : filteredList.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="no-data">No checklists match the current filters.</td>
              </tr>
            ) : (
              filteredList.map((row) => (
                <tr
                  key={row.id}
                  className="clickable-row"
                  onClick={() => openChecklist(row)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openChecklist(row); } }}
                >
                  <td>{clientName(row)}</td>
                  <td>{assigneeName(row)}</td>
                  <td>{row.type_name || row.type_code || '—'}</td>
                  <td>{row.closed ? 'Yes' : 'No'}</td>
                  {canDeleteChecklist && (
                    <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                      <DeleteIconButton
                        className="list-btn-delete"
                        onClick={() => handleDelete(row.id)}
                      />
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </>
      )}
    </div>
  );
}

export default MyChecklists;
